import {Injectable} from '@angular/core';
import {BaseDaoService, handleError} from '@q9elements/ui-kit/common';
import {Observable, tap} from 'rxjs';
import {AccessMonitoringPolicy} from '../models/access-monitoring-policy.model';
import {
  AllowedPollingInterval,
  MonitoringSetupStatus
} from '../models/monitoring-setup-status.model';

export interface SpaceSlackConnection {
  slackAuthUrl: string;
}

export interface SlackInfo {
  connected: boolean;
  slackTeamId: string;
  slackTeamName: string;
  connectedAt: string;
  status: string;
}

export interface PolicyWizardState {
  refModelId: string;
  teamId: string;
  isFirstPolicy: boolean;
  policy?: AccessMonitoringPolicy;
  stepIndex: number;
  formValue: unknown;
  useTemplate?: boolean;
}

export const POLICY_WIZARD_RESUME_PARAM = 'resume';
export const POLICY_WIZARD_RESUME_VALUE = 'policy-wizard';

@Injectable({
  providedIn: 'root'
})
export class AccessMonitoringSetupService extends BaseDaoService {
  private static readonly PENDING_TEAM_ID_KEY = 'acm.slackConnect.pendingTeamId';
  private static readonly PENDING_RETURN_URL_KEY = 'acm.slackConnect.pendingReturnUrl';
  private static readonly POLICY_WIZARD_STATE_KEY = 'acm.policyWizard.pendingState';

  private logSlackConnectError(message: string, err: unknown): void {
    console.error(`[AccessMonitoringSetupService] ${message}`, err);
  }

  public getMonitoringSetupStatus(refModelId: string): Observable<MonitoringSetupStatus> {
    const url = `${this.apiUrl}/refmodel/${refModelId}/change-monitoring/job-config`;
    return this.http.get<MonitoringSetupStatus>(url).pipe(handleError());
  }

  public savePollingInterval(
    refModelId: string,
    pollingIntervalMinutes: AllowedPollingInterval
  ): Observable<void> {
    const url = `${this.apiUrl}/refmodel/${refModelId}/change-monitoring/job-config`;

    return this.http
      .post<void>(url, {
        pollingIntervalMinutes,
        isEnabled: true
      })
      .pipe(handleError());
  }

  public connectSlackWorkspace(teamId: string, interval: string): Observable<SpaceSlackConnection> {
    const url = `${this.apiUrl}/slack/connect/${teamId}`;

    return this.http.get<SpaceSlackConnection>(url).pipe(
      tap(payload => {
        this.setPendingSlackTeamId(teamId);
        this.setPendingSlackReturnUrl(window.location.pathname + `?interval=${interval}`);

        window.location.href = payload.slackAuthUrl;
      }),
      handleError()
    );
  }

  public connectSlackForPolicyWizard(
    teamId: string,
    wizardState: PolicyWizardState
  ): Observable<SpaceSlackConnection> {
    const url = `${this.apiUrl}/slack/connect/${teamId}`;

    return this.http.get<SpaceSlackConnection>(url).pipe(
      tap(payload => {
        this.savePolicyWizardState(wizardState);
        this.setPendingSlackTeamId(teamId);
        this.setPendingSlackReturnUrl(
          `${window.location.pathname}?${POLICY_WIZARD_RESUME_PARAM}=${POLICY_WIZARD_RESUME_VALUE}`
        );

        window.location.href = payload.slackAuthUrl;
      }),
      handleError()
    );
  }

  public getSlackChannels(teamId: string): Observable<any> {
    const url = `${this.apiUrl}/slack/channels/${teamId}`;

    return this.http.get(url).pipe(handleError());
  }

  public getSlackConnection(teamId: string): Observable<SlackInfo> {
    const url = `${this.apiUrl}/slack/connection/${teamId}`;

    return this.http.get<SlackInfo>(url).pipe(handleError());
  }

  public getPendingSlackTeamId(): string | null {
    try {
      return localStorage.getItem(AccessMonitoringSetupService.PENDING_TEAM_ID_KEY);
    } catch (err) {
      this.logSlackConnectError('Failed to read pending Slack teamId from localStorage.', err);
      return null;
    }
  }

  public getPendingSlackReturnUrl(): string | null {
    try {
      return localStorage.getItem(AccessMonitoringSetupService.PENDING_RETURN_URL_KEY);
    } catch (err) {
      this.logSlackConnectError('Failed to read pending Slack returnUrl from localStorage.', err);
      return null;
    }
  }

  public savePolicyWizardState(state: PolicyWizardState): void {
    try {
      sessionStorage.setItem(
        AccessMonitoringSetupService.POLICY_WIZARD_STATE_KEY,
        JSON.stringify(state)
      );
    } catch (err) {
      this.logSlackConnectError('Failed to save policy wizard state to sessionStorage.', err);
    }
  }

  public consumePolicyWizardState(): PolicyWizardState | null {
    try {
      const raw = sessionStorage.getItem(AccessMonitoringSetupService.POLICY_WIZARD_STATE_KEY);
      if (!raw) return null;
      sessionStorage.removeItem(AccessMonitoringSetupService.POLICY_WIZARD_STATE_KEY);
      return JSON.parse(raw) as PolicyWizardState;
    } catch (err) {
      this.logSlackConnectError('Failed to read policy wizard state from sessionStorage.', err);
      return null;
    }
  }

  private setPendingSlackTeamId(teamId: string): void {
    try {
      localStorage.setItem(AccessMonitoringSetupService.PENDING_TEAM_ID_KEY, teamId);
    } catch (err) {
      this.logSlackConnectError('Failed to store pending Slack teamId in localStorage.', err);
    }
  }

  private setPendingSlackReturnUrl(url: string): void {
    try {
      localStorage.setItem(AccessMonitoringSetupService.PENDING_RETURN_URL_KEY, url);
    } catch (err) {
      this.logSlackConnectError('Failed to store pending Slack returnUrl in localStorage.', err);
    }
  }
}
